import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { AuthModal } from "../components/AuthModal";
import { UsernameModal } from "../components/UsernameModal";

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

async function apiPost(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
  return data;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/auth/me`, { credentials: "include" });
      if (res.ok) setUser(await res.json());
      else setUser(null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    const params = new URLSearchParams(window.location.search);
    if (params.get("auth_error")) {
      params.delete("auth_error");
      const qs = params.toString();
      window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, [checkAuth]);

  const login = async (email, password) => {
    const u = await apiPost("/api/auth/login", { email, password });
    setUser(u);
    setAuthOpen(false);
    return u;
  };

  const register = async (username, email, password) => {
    const u = await apiPost("/api/auth/register", { username, email, password });
    setUser(u);
    setAuthOpen(false);
    return u;
  };

  const setUsername = async (username) => {
    const u = await apiPost("/api/auth/set-username", { username });
    setUser(u);
    return u;
  };

  const logout = async () => {
    await apiPost("/api/auth/logout", {}).catch(() => {});
    setUser(null);
  };

  const googleLogin = () => {
    // Redirect to our own backend Google OAuth start endpoint (custom OAuth client).
    window.location.href = `${API}/api/auth/google/start`;
  };

  const value = {
    user, loading, login, register, logout, googleLogin, setUsername,
    openAuth: () => setAuthOpen(true),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <UsernameModal open={!!user && user.needs_username} />
    </AuthContext.Provider>
  );
}
